import { useMemo } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { useTranslation } from '@frontend/i18n';
import { InsuranceStatus, useInsuranceTabShallowStore } from '@frontend/insurance-tab';
import { Photos } from '@frontend/photos';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ListRow, Text } from '@frontend/design-system';
import { getPersonDetailsByAppointment } from '../../utils';

type PatientListRowProps = {
  locationId: string;
  appt?: Appointment | CalendarEvent;
  isScheduleV3FlagEnabled: boolean;
  shouldShowInsuranceStatus?: boolean;
};

export const PatientListRow = ({
  appt,
  locationId,
  shouldShowInsuranceStatus = false,
  isScheduleV3FlagEnabled,
}: PatientListRowProps) => {
  const { t } = useTranslation('schedule-pulse');

  const { setPersonId, setSelectedTab } = useContactPanelShallowStore('setPersonId', 'setSelectedTab');

  const { insuranceStatusByPersonIdsMap } = useInsuranceTabShallowStore('insuranceStatusByPersonIdsMap');

  const { appointmentType, firstName, fullName, lastName, personId, appointmentLocationId, practitionerName } =
    useMemo(() => {
      return getPersonDetailsByAppointment(appt!, isScheduleV3FlagEnabled);
    }, [isScheduleV3FlagEnabled, appt]);

  const handleSetPersonIdAndLocationId = (apptPersonId: string, apptLocationId: string) => {
    if (apptPersonId && apptLocationId) {
      setPersonId(apptPersonId, true, apptLocationId || locationId);
      setSelectedTab('insurance-tab');
    }
  };

  return (
    <ListRow
      css={{ border: 0, padding: theme.spacing(1, 2), borderBottom: `1px solid ${theme.colors.neutral20}` }}
      onClick={() => handleSetPersonIdAndLocationId(personId, appointmentLocationId)}
    >
      <ListRow.Lead css={{ alignItems: 'center', paddingRight: theme.spacing(1) }}>
        <Photos.ContactProfilePhoto
          size='small'
          personId={personId ?? ''}
          firstName={firstName || ''}
          lastName={lastName || ''}
          onClick={() => handleSetPersonIdAndLocationId(personId, appointmentLocationId)}
        />
      </ListRow.Lead>
      <ListRow.Content css={{ padding: theme.spacing(1) }}>
        <Text size='large' weight='bold'>
          {fullName || t('Unknown')}
        </Text>
        <section css={{ display: 'flex', gap: theme.spacing(1), alignItems: 'center' }}>
          {appointmentType && <Text size='small'>{appointmentType ?? '-'}</Text>}
          {practitionerName && (
            <Text size='small' color='subdued'>
              {practitionerName}
            </Text>
          )}
        </section>
      </ListRow.Content>
      <ListRow.Trail
        css={{
          justifySelf: 'end',
          gap: theme.spacing(1),
          paddingLeft: theme.spacing(0.5),
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {shouldShowInsuranceStatus && (
          <InsuranceStatus status={insuranceStatusByPersonIdsMap?.[personId || ''] || ''} />
        )}
      </ListRow.Trail>
    </ListRow>
  );
};
